<template>
  <transition :name="toggleTransition" @after-enter="onEnter" @after-leave="onLeave">
    <div
      class="ui-modal ui-modal__mask"
      :class="classes"
      :role="role"
      v-if="isOpen"
    >
      <div
        class="ui-modal__wrapper"
        ref="backdrop"
        :class="{ 'has-dummy-scrollbar': preventShift }"
        @click="dismissOnBackdrop && closeModal($event)"
      >
        <div
          class="ui-modal__container"
          ref="container"
          tabindex="-1"
          @keydown.esc="dismissOnEsc && closeModal($event)"
        >
          <div class="ui-modal__header" v-if="!removeHeader">
            <slot name="header">
              <h1 class="ui-modal__header-text">{{ title }}</h1>
            </slot>

            <div class="ui-modal__close-button">
              <span
                @click="closeModal"
                v-if="dismissOnCloseButton && !removeCloseButton && dismissible"
              >
                <i class="material-icons">close</i>
              </span>
            </div>
          </div>

          <div class="ui-modal__body">
            <slot></slot>
          </div>

          <div class="ui-modal__footer" v-if="hasFooter">
            <slot name="footer"></slot>
          </div>

          <div
            class="ui-modal__focus-redirect"
            tabindex="0"

            @focus.stop="redirectFocus"
          ></div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
// keen ui
import classlist from 'utils/classlist';

export default {
  name: 'ui-modal',

  props: {
    title: {
      type: String,
      default: 'UiModal title'
    },
    size: {
      type: String,
      default: 'normal' // 'small', 'normal', or 'large'
    },
    role: {
      type: String,
      default: 'dialog' // 'dialog' or 'alertdialog'
    },
    transition: {
      type: String,
      default: 'scale' // 'scale', or 'fade'
    },
    removeHeader: {
      type: Boolean,
      default: false
    },
    removeCloseButton: {
      type: Boolean,
      default: false
    },
    preventShift: {
      type: Boolean,
      default: false
    },
    dismissible: {
      type: Boolean,
      default: true
    },
    dismissOn: {
      type: String,
      default: 'backdrop esc close-button'
    }
  },

  data() {
    return {
      isOpen: false,
      lastfocusedElement: null
    };
  },

  computed: {
    classes() {
      return [
        `ui-modal--size-${this.size}`,
        { 'has-footer': this.hasFooter },
        { 'is-open': this.isOpen }
      ];
    },

    hasFooter() {
      return Boolean(this.$slots.footer);
    },

    toggleTransition() {
      return `ui-modal--transition-${this.transition}`;
    },

    dismissOnBackdrop() {
      return this.dismissOn.indexOf('backdrop') > -1;
    },

    dismissOnCloseButton() {
      return this.dismissOn.indexOf('close-button') > -1;
    },

    dismissOnEsc() {
      return this.dismissOn.indexOf('esc') > -1;
    }
  },

  watch: {
    isOpen() {
      this.$nextTick(() => {
        this[this.isOpen ? 'onOpen' : 'onClose']();
      });
    }
  },

  beforeDestroy() {
    if (this.isOpen) {
      this.teardownModal();
    }
  },

  methods: {
    open() {
      this.isOpen = true;
    },

    close() {
      this.isOpen = false;
    },

    closeModal(e) {
      if (!this.dismissible) {
        return;
      }

      // Make sure the element clicked was the backdrop and not a child whose click
      // event has bubbled up
      if (e.currentTarget === this.$refs.backdrop && e.target !== e.currentTarget) {
        return;
      }

      this.isOpen = false;
    },

    onOpen() {
      this.lastfocusedElement = document.activeElement;
      this.$refs.container.focus();

      classlist.add(document.body, 'ui-modal--is-open');
      document.addEventListener('focus', this.restrictFocus, true);

      this.$emit('open');
    },

    onClose() {
      this.teardownModal();
      this.$emit('close');
    },

    redirectFocus() {
      this.$refs.container.focus();
    },

    restrictFocus(e) {
      if (!this.$refs.container.contains(e.target)) {
        e.stopPropagation();
        this.$refs.container.focus();
      }
    },

    teardownModal() {
      // classlist.remove(document.body, 'ui-modal--is-open');
      document.removeEventListener('focus', this.restrictFocus, true);

      if (this.lastfocusedElement) {
        this.lastfocusedElement.focus();
      }
    },

    onEnter() {
      this.$emit('reveal');
    },

    onLeave() {
      this.$emit('hide');

      classlist.remove(document.body, 'ui-modal--is-open');
    }
  }
};
</script>



// WEBPACK FOOTER //
// Modal.vue?e0f7a6b6